'use client'

import { EASE, HAIRLINE, useReveal } from '@/components/page-kit'
import { AnimatedNumber } from '@/components/ui/animated-number'
import { ChromaText } from '@/components/ui/chroma-text'
import { cn } from '@/lib/utils'

/* The capabilities band: four stats on one ruled surface. The old build set
   every string in a hardcoded #0a2b2e ink — invisible the moment the canvas
   went dark (the "blue section" report was the user selecting the text just
   to read it) — and its count-up printed "< 3" backwards as "3<" by always
   appending the non-digit part after the number. Tokens fixed the ink; the
   count-up is now the shared AnimatedNumber, whose parser is prefix-aware
   ("< 3" stays "< 3") and passes pure symbols (∞) through as plain text. */

const stats = [
  {
    num: '23+',
    label: 'Integrated modules',
    description: 'Every school function covered out of the box',
  },
  {
    num: '< 3',
    label: 'Weeks to go live',
    description: 'Guided setup from discovery call to live system',
  },
  {
    num: '∞',
    label: 'Users included',
    description: 'No per-seat pricing: staff, students & parents unlimited',
  },
  {
    num: '100%',
    label: 'Data ownership',
    description: 'Export or migrate anytime. Your data, always.',
  },
]

function StatCell({
  num,
  label,
  description,
  index,
  shown,
}: (typeof stats)[number] & { index: number; shown: boolean }) {
  return (
    <div
      className="flex h-full flex-col items-center bg-background px-6 py-8 text-center sm:py-10"
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.6s ${EASE} ${index * 90}ms, transform 0.6s ${EASE} ${index * 90}ms`,
      }}
    >
      <p className="font-heading text-4xl font-normal leading-none text-foreground md:text-5xl">
        <AnimatedNumber value={num} delay={index * 90} />
      </p>
      <p className="mt-3 text-[13px] font-semibold uppercase tracking-[0.08em] text-foreground">
        {label}
      </p>
      <p className="mt-1.5 max-w-[17rem] text-[13px] leading-relaxed text-muted-foreground">
        {description}
      </p>
    </div>
  )
}

export function ImpactBand() {
  const { ref, shown } = useReveal<HTMLDivElement>(0.2)

  return (
    <section className="bg-background py-16 sm:py-20 md:py-28">
      <div ref={ref} className="container mx-auto">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-[11px] font-black uppercase tracking-[0.24em] text-emerald-600 dark:text-primary-active">
            System Capabilities
          </p>
          <h2 className="font-heading mt-5 text-2xl font-normal leading-tight sm:text-3xl md:text-4xl">
            <ChromaText>Built for modern educational institutions</ChromaText>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            A comprehensive school management system that streamlines
            operations, enhances communication, and provides actionable insights
          </p>
        </div>

        <div
          className={cn(
            'mt-10 overflow-hidden rounded-xl border sm:mt-12',
            HAIRLINE
          )}
        >
          <div className="grid gap-px bg-border/70 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat, idx) => (
              <StatCell key={stat.label} {...stat} index={idx} shown={shown} />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
